import { Region } from '@ftr/contracts/type/court'
import { CourtSystemId, IndividualId, OrganizationId } from './tenant-id'

/**
 * Individuals do not yet expose a region. Public-app individual context uses this until they do.
 */
export const INDIVIDUAL_DEVELOPMENT_REGION = Region['ap-southeast-2']

// TODO RC-4038 this should move to regional contracts once settled.
export enum TenantType {
  CourtSystem = 'CourtSystem',
  Organization = 'Organization',
  Individual = 'Individual',
  /**
   * No active tenant applies. This represents a logged-in user who is not a member of
   * any court system and has no organization or individual workspace - e.g. an
   * order-only customer on the legacy ftr-web site.
   *
   * Distinct from being logged out: `TenantState.tenantContext` should be
   * `RemoteData.notAsked()` while logged out, and only resolve to `None` once we know
   * the user is authenticated and simply has no tenant.
   *
   * The public app should never resolve to `None` - every authenticated public app user
   * has (or will lazily be given) an individual or organization workspace.
   */
  None = 'None',
}

/**
 * Identifies the tenant workspace the current user is operating in - i.e. which court
 * system, organization, or individual account the navigation, side nav, and "manage my
 * own content" pages are scoped to.
 *
 * IMPORTANT: this is a navigation/UX signal, not an authorization check. Pages that
 * render content belonging to a *different* tenant (customer requests, shared
 * recordings, orders) must continue to authorize via their own resource-specific
 * guard/grant (e.g. a request reference) and resolve the resource's owning tenant id
 * from the resource payload - independent of, and not required to match,
 * `TenantContext`. Viewing content outside your own tenant context is an intentional,
 * supported capability, not a bug.
 */
export type TenantContext =
  | { readonly type: TenantType.CourtSystem; readonly tenantId: CourtSystemId; readonly region: Region }
  | { readonly type: TenantType.Organization; readonly tenantId: OrganizationId; readonly region: Region }
  | { readonly type: TenantType.Individual; readonly tenantId: IndividualId; readonly region: Region }
  | { readonly type: TenantType.None }
